import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, Clock, Lock, Shield } from 'lucide-react';
import LockPill from '../components/common/LockPill';
import InstitutionalActivityDashboard from '../components/super-investors/InstitutionalActivityDashboard';
import { useTheme } from '../components/ThemeContext';
import { useContext } from 'react';
import { UserContext } from '../context/UserContext';
import StockService from '../Api_Module/Api_Services/StockService';
import { formatNumber } from '../utils/Numberformat';

type SuperInvestor = {
  cik: string;
  ceo: string;
  investorName: string;
  dateStart: string;
  dateEnd: string;
  beginMarketValue: number;
  endMarketValue: number;
  years: number;
  cagrPercentage: number;
  stabilityScore: number;
  holdings?: string[];
};

type AumLeaderboardItem = {
  rank: number;
  cik: string;
  name: string;
  date: string;
  year: number;
  quarter: number;
  aum: number;
  aumGrowth: number;
  aumGrowthPercentage: number;
  quarterPerformance: number;
  quarterPerformancePercentage: number;
  threeYearReturnPercentage: number;
  holdings: number;
  portfolioTurnover: number;
  aumDisplay?: string;
  aumGrowthDisplay?: string;
  aumGrowthPercentageDisplay?: string;
  quarterPerformanceDisplay?: string;
  quarterPerformancePercentageDisplay?: string;
  threeYearReturnPercentageDisplay?: string;
  portfolioTurnoverDisplay?: string;
  cagr?: number;
  cagrDisplay?: string;
};

type AumLeaderboardResponse = {
  year: number;
  quarter: number;
  page: number;
  size: number;
  total: number;
  totalPages: number;
  items: AumLeaderboardItem[];
};

const initialsOf = (name: string): string => {
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const colorFromString = (s: string): string => {
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 80% 45%)`;
};

const quarterOf = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()} Q${q}`;
};

const SuperInvestors: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { userData } = useContext(UserContext) || {};
  const isLoggedIn = Boolean(sessionStorage.getItem('STOCK_AUTH_TOKEN'));
  const hasAccess = isLoggedIn && (userData?.planActive === true || userData?.subscriptionStatus === 'active' || userData?.subscriptionStatus === 'trialing');
  const location = useLocation();
  const [data, setData] = React.useState<SuperInvestor[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [sortKey, setSortKey] = React.useState<'stability' | 'cagr' | 'value' | 'years'>('stability');
  const [view, setView] = React.useState<'dashboard' | 'cards' | 'leaderboard' | 'timeline' | 'map'>((location.state as any)?.view || 'dashboard');
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [selectedQuarter, setSelectedQuarter] = React.useState<string>('All');
  const [aumFilter, setAumFilter] = React.useState<string>('all');
  const [minYears, setMinYears] = React.useState<string>('all');
  const [minCagr, setMinCagr] = React.useState<string>('all');
  const [minHoldings, setMinHoldings] = React.useState<string>('all');
  const [maxHoldings, setMaxHoldings] = React.useState<string>('all');
  // AUM leaderboard state
  const [aumData, setAumData] = React.useState<AumLeaderboardResponse | null>(null);
  const [aumLoading, setAumLoading] = React.useState<boolean>(false);
  const [aumPage, setAumPage] = React.useState<number>(0);
  const [aumSize, setAumSize] = React.useState<number>(25);
  const [aumSortBy, setAumSortBy] = React.useState<string>('holdings');
  const [aumDirection, setAumDirection] = React.useState<'asc' | 'desc'>('desc');
  // New filter controls for AUM table
  const [ptUpper, setPtUpper] = React.useState<string>('all'); // portfolio turnover upper bound
  const [aumMin, setAumMin] = React.useState<string>('all'); // replace/alias for aumFilter in AUM table context
  const [minHoldingsAum, setMinHoldingsAum] = React.useState<string>('all');
  const [maxHoldingsAum, setMaxHoldingsAum] = React.useState<string>('all');
  const [minCagrAum, setMinCagrAum] = React.useState<string>('all');

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await StockService.getSuperInvestorsByRank();
        if (!mounted) return;
        const arr: SuperInvestor[] = Array.isArray(res) ? res : res?.data || [];
        setData(arr);
      } catch (e) {
        setError('Failed to load super investors');
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filteredSorted = React.useMemo(() => {
    const q = query.trim().toLowerCase();

    const minAum = aumFilter === '1B' ? 1e9
      : aumFilter === '5B' ? 5e9
        : aumFilter === '10B' ? 1e10
          : aumFilter === '100B' ? 1e11
            : 0;
    const minYearsNum = minYears === '5' ? 5 : minYears === '10' ? 10 : minYears === '15' ? 15 : 0;
    const minCagrNum = minCagr === '10' ? 10 : minCagr === '25' ? 25 : minCagr === '50' ? 50 : 0;
    const minHoldingsNum = minHoldings === '10' ? 10 : minHoldings === '25' ? 25 : minHoldings === '50' ? 50 : 0;
    const maxHoldingsNum = maxHoldings === '100' ? 100 : maxHoldings === '200' ? 200 : maxHoldings === '500' ? 500 : Infinity;

    const filtered = data.filter((d) => {
      const matchText = d.investorName.toLowerCase().includes(q) || d.ceo.toLowerCase().includes(q);
      const matchQuarter = selectedQuarter === 'All' || quarterOf(d.dateEnd) === selectedQuarter;
      const matchAum = (d.endMarketValue || 0) >= minAum;
      const matchYears = (d.years || 0) >= minYearsNum;
      const matchCagr = (d.cagrPercentage || 0) >= minCagrNum;
      const holdingsCount = Array.isArray(d.holdings) ? d.holdings.length : 0;
      const matchHoldingsMin = holdingsCount >= minHoldingsNum;
      const matchHoldingsMax = holdingsCount <= maxHoldingsNum;
      return matchText && matchQuarter && matchAum && matchYears && matchCagr && matchHoldingsMin && matchHoldingsMax;
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === 'stability') return (b.stabilityScore || 0) - (a.stabilityScore || 0);
      if (sortKey === 'cagr') return (b.cagrPercentage || 0) - (a.cagrPercentage || 0);
      if (sortKey === 'value') return (b.endMarketValue || 0) - (a.endMarketValue || 0);
      if (sortKey === 'years') return (b.years || 0) - (a.years || 0);
      return 0;
    });
    return sorted;
  }, [data, query, sortKey, selectedQuarter, aumFilter, minYears, minCagr, minHoldings, maxHoldings]);

  const featured = React.useMemo(() => filteredSorted.slice(0, 5), [filteredSorted]);
  const topCards = React.useMemo(() => {
    return [...data]
      .sort((a, b) => (b.cagrPercentage || 0) - (a.cagrPercentage || 0))
      .slice(0, 9);
  }, [data]);
  const quarterOptions = React.useMemo(() => {
    const set = new Set<string>();
    data.forEach(d => { const q = quarterOf(d.dateEnd); if (q !== '—') set.add(q); });
    const arr = Array.from(set);
    const key = (s: string) => { const parts = s.split(' '); const year = parseInt(parts[0], 10); const qnum = parseInt(parts[1].replace('Q', ''), 10); return year * 10 + qnum; };
    arr.sort((a, b) => key(b) - key(a));
    return arr.slice(0, 12);
  }, [data]);

  React.useEffect(() => {
    if (quarterOptions.length > 0 && selectedQuarter === 'All') {
      setSelectedQuarter(quarterOptions[0]);
    }
  }, [quarterOptions]);

  const selectedYQ = React.useMemo(() => {
    if (!selectedQuarter || selectedQuarter === 'All') return null as null | { year: number; quarter: number };
    const [y, qPart] = selectedQuarter.split(' ');
    const year = parseInt(y, 10);
    const quarter = parseInt(qPart?.replace('Q', ''), 10);
    if (!year || !quarter) return null;
    return { year, quarter };
  }, [selectedQuarter]);

  React.useEffect(() => {
    if (!selectedYQ) return;
    setAumLoading(true);
    (async () => {
      try {
        const res = await StockService.getAumLeaderboard({
          year: selectedYQ.year,
          quarter: selectedYQ.quarter,
          page: aumPage,
          size: aumSize,
          sortBy: aumSortBy,
          direction: aumDirection,
          query,
        });
        setAumData(res);
      } catch (e) {
        // swallow for now; could add error UI
      } finally {
        setAumLoading(false);
      }
    })();
  }, [selectedYQ, aumPage, aumSize, aumSortBy, aumDirection, query]);

  const bgBase = theme === 'dark' ? 'bg-gray-900' : 'bg-white';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const cardBorder = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';
  const cardBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';

  const stabilityToColor = (s: number) => {
    if (s >= 120) return 'bg-green-500';
    if (s >= 90) return 'bg-emerald-400';
    if (s >= 60) return 'bg-yellow-400';
    if (s >= 30) return 'bg-orange-400';
    return 'bg-red-500';
  };

  const podiumClass = (rank: number) => {
    // Removed rank-based background highlighting for consistency across rows
    return '';
  };

  const ViewTabs = (
    <div className="flex flex-wrap gap-2">
      {[
        { k: 'dashboard', label: 'Dashboard' },
        { k: 'cards', label: 'Leaderboard' },
        { k: 'leaderboard', label: 'Portfolios' },
      ].map((t) => (
        <button
          key={t.k}
          onClick={() => setView(t.k as any)}
          className={`${view === t.k ? 'bg-theme-primary text-white active-bg' : `${cardBg} ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} border ${cardBorder}`} px-3 py-1.5 rounded-full text-sm transition`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  // Global loading block removed to allow Dashboard to render immediately


  if (error) {
    return (
      <div className={`min-h-screen ${bgBase}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className={`rounded-lg border ${cardBorder} ${cardBg} p-8`}>
            <div className={`text-sm ${textSecondary}`}>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgBase} overflow-x-hidden`}>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="mb-6">
          {ViewTabs}
        </div>

        {view === 'leaderboard' && (
          <>
            <div className={`mb-4`}>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter funds by name..."
                className={`w-full ${cardBg} border ${cardBorder} rounded-md px-3 py-2 ${theme === 'dark' ? 'text-theme-primary placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
              />
            </div>

            <div className="mb-6 flex items-center gap-2 flex-wrap">
              {/* Sort controls */}
              <div className="flex items-center gap-2">
                <span className={`text-sm ${textSecondary}`}>Sort by</span>
                <select
                  value={aumSortBy}
                  onChange={(e) => { setAumSortBy(e.target.value); setAumPage(0); }}
                  className={`${cardBg} border ${cardBorder} rounded-full px-3 py-2 text-sm ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}
                >
                  <option value="aum">AUM</option>
                  <option value="aumGrowth">AUM Growth</option>
                  <option value="aumGrowthPercentage">AUM Growth %</option>
                  <option value="quarterPerformancePercentage">Quarter Est. Return</option>
                  <option value="threeYearReturnPercentage">3 Year Est. Return</option>
                  <option value="holdings">Holdings</option>
                  <option value="portfolioTurnover">Portfolio Turnover</option>
                  <option value="cagr">CAGR</option>
                </select>
                <button
                  type="button"
                  className={`px-2 py-1 rounded-full border ${cardBorder} text-xs ${theme === 'dark' ? 'text-theme-secondary' : 'text-gray-700'}`}
                  onClick={() => { setAumDirection((d) => (d === 'asc' ? 'desc' : 'asc')); setAumPage(0); }}
                  title={`Direction: ${aumDirection.toUpperCase()}`}
                >
                  {aumDirection === 'asc' ? '↑' : '↓'}
                </button>
              </div>
              {/* New filter chips */}
              <select value={aumMin} onChange={(e) => { setAumMin(e.target.value); setAumPage(0); }} className={`${cardBg} border ${cardBorder} rounded-full px-3 py-2 text-sm ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
                <option value="all">AUM</option>
                <option value="1B">≥ $1B</option>
                <option value="10B">≥ $10B</option>
                <option value="100B">≥ $100B</option>
                <option value="1T">≥ $1T</option>
              </select>
              <select value={ptUpper} onChange={(e) => { setPtUpper(e.target.value); setAumPage(0); }} className={`${cardBg} border ${cardBorder} rounded-full px-3 py-2 text-sm ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
                <option value="all">Portfolio Turnover</option>
                <option value="20">Less than 20%</option>
                <option value="40">Less than 40%</option>
                <option value="60">Less than 60%</option>
                <option value="80">Less than 80%</option>
              </select>
              <select value={minHoldingsAum} onChange={(e) => { setMinHoldingsAum(e.target.value); setAumPage(0); }} className={`${cardBg} border ${cardBorder} rounded-full px-3 py-2 text-sm ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
                <option value="all">Min Holdings</option>
                <option value="100">≥ 100</option>
                <option value="1000">≥ 1,000</option>
                <option value="5000">≥ 5,000</option>
              </select>
              <select value={maxHoldingsAum} onChange={(e) => { setMaxHoldingsAum(e.target.value); setAumPage(0); }} className={`${cardBg} border ${cardBorder} rounded-full px-3 py-2 text-sm ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
                <option value="all">Max Holdings</option>
                <option value="1000">≤ 1,000</option>
                <option value="5000">≤ 5,000</option>
                <option value="10000">≤ 10,000</option>
              </select>
              <select value={minCagrAum} onChange={(e) => { setMinCagrAum(e.target.value); setAumPage(0); }} className={`${cardBg} border ${cardBorder} rounded-full px-3 py-2 text-sm ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
                <option value="all">Min CAGR</option>
                <option value="20">≥ 20%</option>
                <option value="40">≥ 40%</option>
                <option value="80">≥ 80%</option>
                <option value="100">≥ 100%</option>
              </select>
              <button onClick={() => { setAumMin('all'); setPtUpper('all'); setMinHoldingsAum('all'); setMaxHoldingsAum('all'); setMinCagrAum('all'); setAumPage(0); }} className={`px-3 py-2 rounded-full text-sm border ${cardBorder} ${theme === 'dark' ? 'text-theme-secondary' : 'text-gray-700'}`}>Clear</button>
            </div>
          </>
        )}


        {view === 'dashboard' && <InstitutionalActivityDashboard />}

        {view === 'cards' && loading && (
          <div className={`rounded-lg border ${cardBorder} ${cardBg} p-8 flex items-center justify-center`}>
            <span className={textSecondary}>Loading super investors…</span>
          </div>
        )}
        {view === 'cards' && !loading && (
          <>
            {/* Ranking info header */}
            <div className={`mb-6 p-4 rounded-xl border ${cardBorder} ${cardBg}`}>
              <div className="flex items-center justify-center gap-2 text-sm">
                <TrendingUp className={`w-4 h-4 ${textSecondary}`} />
                <span className={textSecondary}>Ranked by</span>
                <span className={`font-semibold ${textPrimary}`}>Stability Score</span>
                <span className={textSecondary}>and</span>
                <span className={`font-semibold ${textPrimary}`}>CAGR Performance</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSorted.map((inv) => {
                const isOpen = expanded === inv.cik;
                const startYear = new Date(inv.dateStart).getFullYear();
                const endYear = new Date(inv.dateEnd).getFullYear();
                const years = Math.round(inv.years);
                return (
                  <div
                    key={inv.cik}
                    className={`border ${cardBorder} ${cardBg} rounded-xl p-4 shadow-sm hover:shadow-md transition cursor-pointer`}
                    onClick={() => navigate(`/institution/${inv.cik}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-theme-primary text-sm font-bold"
                          style={{ background: colorFromString(inv.investorName) }}
                        >
                          {initialsOf(inv.ceo || inv.investorName)}
                        </div>
                        <div>
                          <div className={`font-semibold ${textPrimary}`}>{inv.ceo}</div>
                          <div className={`text-xs ${textSecondary}`}>{inv.investorName}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => setExpanded(isOpen ? null : inv.cik)}
                        className={`text-xs px-2 py-1 rounded-full border ${cardBorder} ${theme === 'dark' ? 'text-theme-secondary' : 'text-gray-700'}`}
                      >
                        {isOpen ? 'Hide' : 'Details'}
                      </button>
                    </div>

                    <div className="mt-4">
                      <div className={`text-xs uppercase tracking-wide mb-1 ${textSecondary}`}>Assets Under Management</div>
                      <div className="flex items-baseline gap-2">
                        <div className={`text-base font-bold ${textPrimary}`}>${formatNumber(inv.endMarketValue, 2)}</div>
                        <div className={`text-xs ${textSecondary}`}>{quarterOf(inv.dateEnd)}</div>
                      </div>
                    </div>

                    {/* CAGR Performance - Make it prominent */}
                    <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className={`text-xs ${textSecondary} mb-1`}>
                            {years}-Year CAGR ({startYear}–{endYear})
                          </div>
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                            {hasAccess ? (
                              <span className={`text-2xl font-bold text-green-600 dark:text-green-400`}>
                                {Math.abs(Math.round(inv.cagrPercentage))}%
                              </span>
                            ) : (
                              <div className="flex items-center gap-1 text-theme-muted" title="Login to view CAGR">
                                <Lock className="w-4 h-4" />
                                <span className="text-sm font-medium">Login to view</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stability Score */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <Shield className={`w-3 h-3 ${textSecondary}`} />
                          <span className={`text-xs ${textSecondary}`}>Stability Score</span>
                        </div>
                        {hasAccess ? (
                          <span className={`text-sm font-semibold ${textPrimary}`}>{inv.stabilityScore.toFixed(1)}</span>
                        ) : (
                          <Lock className="w-3 h-3 text-theme-muted" />
                        )}
                      </div>
                      <div className="h-2 rounded-full bg-gray-200 dark:bg-theme-tertiary overflow-hidden">
                        {hasAccess ? (
                          <div
                            className={`${stabilityToColor(inv.stabilityScore)} h-2`}
                            style={{ width: `${Math.min(100, (inv.stabilityScore / 150) * 100)}%` }}
                          />
                        ) : (
                          <div className="h-2 w-full bg-gray-300 dark:bg-gray-700 opacity-30" />
                        )}
                      </div>
                    </div>

                    {inv.holdings && inv.holdings.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {inv.holdings.slice(0, 6).map((h) => (
                          <span key={h} className={`px-2 py-1 rounded-full text-xs border ${cardBorder} ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>[{h}]</span>
                        ))}
                      </div>
                    )}

                    {isOpen && (
                      <div className={`mt-4 border-t ${cardBorder} pt-3 text-sm`}>
                        <div className="grid grid-cols-2 gap-2">
                          <div className={`${textSecondary}`}>Start</div>
                          <div className={`${textPrimary}`}>{new Date(inv.dateStart).getFullYear()}</div>
                          <div className={`${textSecondary}`}>End</div>
                          <div className={`${textPrimary}`}>{new Date(inv.dateEnd).getFullYear()}</div>
                          <div className={`${textSecondary}`}>Begin Value</div>
                          <div className={`${textPrimary}`}>${formatNumber(inv.beginMarketValue, 2)}</div>
                          <div className={`${textSecondary}`}>End Value</div>
                          <div className={`${textPrimary}`}>${formatNumber(inv.endMarketValue, 2)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
              }
            </div>
          </>
        )}

        {view === 'leaderboard' && (
          <div className={`border ${cardBorder} ${cardBg} rounded-xl overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className={`${theme === 'dark' ? 'bg-gray-700/40' : 'bg-gray-50'} text-xs`}>
                  <tr>
                    <th className="text-left px-4 py-2">#</th>
                    <th className="text-left px-4 py-2">Name</th>
                    <th className="text-right px-4 py-2">
                      <button
                        className={`inline-flex items-center gap-1 ${aumSortBy === 'aum' ? 'text-theme-primary font-semibold' : ''}`}
                        title="Sort by AUM"
                        onClick={() => { setAumPage(0); setAumDirection(aumSortBy === 'aum' ? (aumDirection === 'asc' ? 'desc' : 'asc') : 'desc'); setAumSortBy('aum'); }}
                      >
                        AUM {aumSortBy === 'aum' ? (aumDirection === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="text-right px-4 py-3">
                      <button
                        className={`inline-flex items-center gap-1 ${aumSortBy === 'aumGrowth' ? 'text-theme-primary font-semibold' : ''}`}
                        title="Sort by AUM Growth"
                        onClick={() => { setAumPage(0); setAumDirection(aumSortBy === 'aumGrowth' ? (aumDirection === 'asc' ? 'desc' : 'asc') : 'desc'); setAumSortBy('aumGrowth'); }}
                      >
                        AUM Growth {aumSortBy === 'aumGrowth' ? (aumDirection === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="text-right px-4 py-3">
                      <button
                        className={`inline-flex items-center gap-1 ${aumSortBy === 'aumGrowthPercentage' ? 'text-theme-primary font-semibold' : ''}`}
                        title="Sort by AUM Growth %"
                        onClick={() => { setAumPage(0); setAumDirection(aumSortBy === 'aumGrowthPercentage' ? (aumDirection === 'asc' ? 'desc' : 'asc') : 'desc'); setAumSortBy('aumGrowthPercentage'); }}
                      >
                        AUM Growth % {aumSortBy === 'aumGrowthPercentage' ? (aumDirection === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="text-right px-4 py-3">
                      <button
                        className={`inline-flex items-center gap-1 ${aumSortBy === 'quarterPerformancePercentage' ? 'text-theme-primary font-semibold' : ''}`}
                        title="Sort by Quarter Est. Return"
                        onClick={() => { setAumPage(0); setAumDirection(aumSortBy === 'quarterPerformancePercentage' ? (aumDirection === 'asc' ? 'desc' : 'asc') : 'desc'); setAumSortBy('quarterPerformancePercentage'); }}
                      >
                        Quarter Est. Return {aumSortBy === 'quarterPerformancePercentage' ? (aumDirection === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="text-right px-4 py-3">
                      <button
                        className={`inline-flex items-center gap-1 ${aumSortBy === 'threeYearReturnPercentage' ? 'text-theme-primary font-semibold' : ''}`}
                        title="Sort by 3 Year Est. Return"
                        onClick={() => { setAumPage(0); setAumDirection(aumSortBy === 'threeYearReturnPercentage' ? (aumDirection === 'asc' ? 'desc' : 'asc') : 'desc'); setAumSortBy('threeYearReturnPercentage'); }}
                      >
                        3 Year Est. Return {aumSortBy === 'threeYearReturnPercentage' ? (aumDirection === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="text-right px-4 py-3">
                      <button
                        className={`inline-flex items-center gap-1 ${aumSortBy === 'cagr' ? 'text-theme-primary font-semibold' : ''}`}
                        title="Sort by CAGR"
                        onClick={() => { setAumPage(0); setAumDirection(aumSortBy === 'cagr' ? (aumDirection === 'asc' ? 'desc' : 'asc') : 'desc'); setAumSortBy('cagr'); }}
                      >
                        CAGR {aumSortBy === 'cagr' ? (aumDirection === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="text-right px-4 py-3">
                      <button
                        className={`inline-flex items-center gap-1 ${aumSortBy === 'holdings' ? 'text-theme-primary font-semibold' : ''}`}
                        title="Sort by Holdings"
                        onClick={() => { setAumPage(0); setAumDirection(aumSortBy === 'holdings' ? (aumDirection === 'asc' ? 'desc' : 'asc') : 'desc'); setAumSortBy('holdings'); }}
                      >
                        Holdings {aumSortBy === 'holdings' ? (aumDirection === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="text-right px-4 py-3">
                      <button
                        className={`inline-flex items-center gap-1 ${aumSortBy === 'portfolioTurnover' ? 'text-theme-primary font-semibold' : ''}`}
                        title="Sort by Portfolio Turnover"
                        onClick={() => { setAumPage(0); setAumDirection(aumSortBy === 'portfolioTurnover' ? (aumDirection === 'asc' ? 'desc' : 'asc') : 'desc'); setAumSortBy('portfolioTurnover'); }}
                      >
                        Portfolio Turnover {aumSortBy === 'portfolioTurnover' ? (aumDirection === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const minAumVal = aumMin === '1B' ? 1e9 : aumMin === '10B' ? 1e10 : aumMin === '100B' ? 1e11 : aumMin === '1T' ? 1e12 : 0;
                    const ptUpperVal = ptUpper === '20' ? 0.20 : ptUpper === '40' ? 0.40 : ptUpper === '60' ? 0.60 : ptUpper === '80' ? 0.80 : null;
                    const minHoldVal = minHoldingsAum === '100' ? 100 : minHoldingsAum === '1000' ? 1000 : minHoldingsAum === '5000' ? 5000 : 0;
                    const maxHoldVal = maxHoldingsAum === '1000' ? 1000 : maxHoldingsAum === '5000' ? 5000 : maxHoldingsAum === '10000' ? 10000 : Infinity;
                    const minCagrVal = minCagrAum === '20' ? 20 : minCagrAum === '40' ? 40 : minCagrAum === '80' ? 80 : minCagrAum === '100' ? 100 : 0;
                    const items = (aumLoading ? [] : (aumData?.items || [])).filter((it) => {
                      const aOk = (it.aum || 0) >= minAumVal;
                      const pOk = ptUpperVal == null ? true : (it.portfolioTurnover || 0) <= ptUpperVal;
                      const hOk = (it.holdings || 0) >= minHoldVal && (it.holdings || 0) <= maxHoldVal;
                      const cOk = (it.cagr || 0) >= minCagrVal;
                      return aOk && pOk && hOk && cOk;
                    });
                    return items.map((it) => (
                      <tr
                        key={`${it.cik}-${it.rank}`}
                        className={`${podiumClass(it.rank)} border-t ${cardBorder} cursor-pointer hover:${theme === 'dark' ? 'bg-white/5' : 'bg-gray-50'} transition`}
                        onClick={() => navigate(`/institution/${it.cik}`)}
                      >
                        <td className="px-4 py-3 font-semibold">{it.rank}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-theme-primary text-xs font-bold"
                              style={{ background: colorFromString(it.name) }}
                            >
                              {initialsOf(it.name)}
                            </div>
                            <div className={`${textPrimary}`}>{it.name}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">{it.aumDisplay ?? `$${formatNumber(it.aum, 2)}`}</td>
                        <td className="px-4 py-3 text-right">{it.aumGrowthDisplay ?? `${it.aumGrowth >= 0 ? '+' : ''}$${formatNumber(Math.abs(it.aumGrowth), 2)}`}</td>
                        <td className="px-4 py-3 text-right">{it.aumGrowthPercentageDisplay ?? `${(it.aumGrowthPercentage ?? 0).toFixed(2)}%`}</td>
                        <td className="px-4 py-3 text-right">
                          {hasAccess ? (
                            <span>{it.quarterPerformancePercentageDisplay ?? `${(it.quarterPerformancePercentage ?? 0).toFixed(2)}%`}</span>
                          ) : (
                            <div className="flex justify-end">
                              <LockPill size="sm" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {hasAccess ? (
                            <span>{it.threeYearReturnPercentageDisplay ?? `${(it.threeYearReturnPercentage ?? 0).toFixed(2)}%`}</span>
                          ) : (
                            <div className="flex justify-end">
                              <LockPill size="sm" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {hasAccess ? (
                            <span>{it.cagrDisplay ?? (it.cagr ? `${it.cagr.toFixed(2)}%` : '-')}</span>
                          ) : (
                            <div className="flex justify-end">
                              <LockPill size="sm" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">{it.holdings?.toLocaleString?.() ?? it.holdings}</td>
                        <td className="px-4 py-3 text-right">
                          {hasAccess ? (
                            <span>{it.portfolioTurnoverDisplay ?? `${(it.portfolioTurnover ?? 0).toFixed(2)}%`}</span>
                          ) : (
                            <div className="flex justify-end">
                              <LockPill size="sm" />
                            </div>
                          )}
                        </td>
                      </tr>
                    ));
                  })()}
                  {aumLoading && (
                    <tr>
                      <td colSpan={10} className="px-4 py-6 text-center">
                        <span className={textSecondary}>Loading AUM leaderboard…</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className={`flex items-center justify-between px-4 py-3 border-t ${cardBorder}`}>
              <div className={`text-xs ${textSecondary}`}>
                Page {(aumData?.page ?? aumPage) + 1} of {aumData?.totalPages ?? 1} • {aumData?.total?.toLocaleString?.() ?? ''} results
              </div>
              <div className="flex items-center gap-2">
                <button
                  className={`px-3 py-1.5 rounded-md border ${cardBorder} ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                  disabled={(aumData?.page ?? aumPage) <= 0 || aumLoading}
                  onClick={() => setAumPage((p) => Math.max(0, p - 1))}
                >
                  Prev
                </button>
                <button
                  className={`px-3 py-1.5 rounded-md border ${cardBorder} ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                  disabled={aumLoading || ((aumData?.page ?? aumPage) + 1 >= (aumData?.totalPages ?? 1))}
                  onClick={() => setAumPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {view === 'timeline' && loading && (
          <div className={`rounded-lg border ${cardBorder} ${cardBg} p-8 flex items-center justify-center`}>
            <span className={textSecondary}>Loading super investors…</span>
          </div>
        )}
        {view === 'timeline' && !loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSorted.map((inv) => {
              const startY = new Date(inv.dateStart).getFullYear();
              const endY = new Date(inv.dateEnd).getFullYear();
              const tenure = Math.max(1, Math.round(inv.years));
              return (
                <div key={inv.cik} className={`border ${cardBorder} ${cardBg} rounded-xl p-4`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ background: colorFromString(inv.investorName) }}
                    >
                      {initialsOf(inv.ceo || inv.investorName)}
                    </div>
                    <div>
                      <div className={`font-semibold ${textPrimary}`}>{inv.ceo}</div>
                      <div className={`text-xs ${textSecondary}`}>{inv.investorName}</div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="relative">
                      <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-300 dark:bg-gray-700" />
                      <div className="relative">
                        <div className="w-6 h-6 rounded-full border-2 border-transparent" style={{ background: colorFromString(inv.investorName) }} />
                        <div className={`ml-8 text-sm ${textPrimary}`}>{startY}</div>
                      </div>
                      <div className="mt-6 space-y-6">
                        {Array.from({ length: Math.min(3, Math.max(1, Math.floor(tenure / 5))) }).map((_, i) => (
                          <div key={i} className="relative">
                            <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-600 absolute left-4 top-1" />
                            <div className={`ml-8 text-xs ${textSecondary}`}>Milestone</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 relative">
                        <div className="w-6 h-6 rounded-full border-2 border-transparent" style={{ background: colorFromString(inv.investorName) }} />
                        <div className={`ml-8 text-sm ${textPrimary}`}>{endY}</div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className={`text-sm ${textSecondary}`}>Market Value</div>
                      <div className={`text-xl font-bold ${textPrimary}`}>${formatNumber(inv.endMarketValue, 2)}</div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                        <div className={`flex items-center gap-1 ${textSecondary}`}>
                          <TrendingUp className="w-4 h-4" />
                          {hasAccess ? (
                            <span className={`${textPrimary} font-semibold`}>{Math.round(inv.cagrPercentage)}%</span>
                          ) : (
                            <Lock className="w-3 h-3" />
                          )}
                        </div>
                        <div className={`flex items-center gap-1 ${textSecondary}`}>
                          <Clock className="w-4 h-4" />
                          <span className={`${textPrimary} font-semibold`}>{Math.round(inv.years)}</span>
                        </div>
                        <div className={`flex items-center gap-1 ${textSecondary}`}>
                          <span>🛡️</span>
                          {hasAccess ? (
                            <span className={`${textPrimary} font-semibold`}>{inv.stabilityScore.toFixed(1)}</span>
                          ) : (
                            <Lock className="w-3 h-3" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {view === 'map' && (
          <div className={`border ${cardBorder} ${cardBg} rounded-xl p-6`}>
            <div className={`text-sm ${textSecondary}`}>Map view requires a mapping of each investor to a style cluster such as Value, Growth, Quant, or Activist. Provide a mapping to enable clustered nodes with tooltips.</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperInvestors;